import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, FlaskConical, Loader2, RotateCcw, Search, ShieldCheck, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = process.env.REACT_APP_BACKEND_URL;
const call = async (path, opts = {}) => {
  const r = await fetch(`${API}/api${path}`, { credentials: "include", headers: { "Content-Type": "application/json" }, ...opts });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Request failed");
  return d;
};
const ROLES = [["portfolio_manager", "Portfolio Manager"], ["school_admin", "School Administrator"]];
const sel = "h-10 rounded-md border border-[#e5e7eb] bg-white px-2 text-sm";

const Workspaces = ({ ws, reload }) => {
  const [busy, setBusy] = useState("");
  const run = async (key, fn, msg) => {
    setBusy(key);
    try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };
  return (
    <div className="space-y-2" data-testid="demo-workspaces">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-[#111827] flex items-center gap-1.5"><FlaskConical className="h-4 w-4 text-amber-600" />Personal demo workspaces</h3>
        <Button size="sm" variant="outline" disabled={!!busy} data-testid="demo-ws-bootstrap"
          onClick={() => run("boot", () => call("/admin/demo-workspaces/bootstrap", { method: "POST" }), "Checked Ron and Dirk")}>
          {busy === "boot" && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Set up Ron &amp; Dirk
        </Button>
      </div>
      <ul className="divide-y divide-[#f0f1f3] text-[13px]">
        {ws.people.map((p) => (
          <li key={p.email} className="flex flex-wrap justify-between gap-2 py-1.5" data-testid={`demo-person-${p.email}`}>
            <span>{p.name || p.email} <span className="text-[#9ca3af]">{p.email}</span></span>
            <span className={p.status === "ready" ? "text-emerald-700" : "text-amber-700"}>
              {p.status === "pending_account" ? "Pending — no account with this exact email" : p.status === "ready" ? "Workspace ready" : "No workspace yet"}
            </span>
          </li>
        ))}
        {ws.workspaces.map((w) => (
          <li key={w.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5" data-testid={`demo-ws-${w.id}`}>
            <span><b>{w.name || w.id}</b> · {w.owner_email} · gen {w.generation} · {w.status}</span>
            <Button size="sm" variant="outline" disabled={!!busy} data-testid={`demo-ws-reset-${w.id}`}
              onClick={() => window.confirm(`Reset ${w.owner_email}'s demo to the starting dataset?`) &&
                run(w.id, () => call(`/admin/demo-workspaces/${w.id}/reset`, { method: "POST" }), "Workspace reset")}>
              {busy === w.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const AddForm = ({ user, workspaces, onAdded }) => {
  const [f, setF] = useState({ role: "portfolio_manager", environment: "demo", workspace_id: workspaces[0]?.id || "", org_id: "", is_default: false });
  const [orgs, setOrgs] = useState([]);
  useEffect(() => {
    const q = new URLSearchParams({ role: f.role, environment: f.environment, ...(f.environment === "demo" ? { workspace_id: f.workspace_id } : {}) });
    if (f.environment === "demo" && !f.workspace_id) return setOrgs([]);
    call(`/admin/role-assignments/orgs?${q}`).then((d) => setOrgs(d.orgs)).catch(() => setOrgs([]));
  }, [f.role, f.environment, f.workspace_id]);
  const add = async () => {
    try {
      await call("/admin/role-assignments", { method: "POST", body: JSON.stringify({ ...f, user_id: user.user_id, workspace_id: f.environment === "demo" ? f.workspace_id : null }) });
      toast.success("Assignment added"); onAdded();
    } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5" data-testid="assign-form">
      <select className={sel} value={f.role} onChange={(e) => setF({ ...f, role: e.target.value, org_id: "" })} data-testid="assign-role">
        {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <select className={sel} value={f.environment} onChange={(e) => setF({ ...f, environment: e.target.value, org_id: "" })} data-testid="assign-env">
        <option value="demo">Demo</option><option value="production">Production</option>
      </select>
      {f.environment === "demo" ? (
        <select className={sel} value={f.workspace_id} onChange={(e) => setF({ ...f, workspace_id: e.target.value, org_id: "" })} data-testid="assign-workspace">
          <option value="">Demo workspace…</option>
          {workspaces.map((w) => <option key={w.id} value={w.id}>{w.name || w.id} ({w.owner_email})</option>)}
        </select>
      ) : <div className="hidden lg:block" />}
      <select className={sel} value={f.org_id} onChange={(e) => setF({ ...f, org_id: e.target.value })} data-testid="assign-org">
        <option value="">{f.role === "portfolio_manager" ? "Portfolio…" : "School…"}</option>
        {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>
      <Button className="h-10 bg-[#007a9e] hover:bg-[#00607d] text-white" disabled={!f.org_id} onClick={add} data-testid="assign-add">Add assignment</Button>
    </div>
  );
};

const UserAssignments = ({ user, workspaces }) => {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => call(`/admin/role-assignments?user_id=${user.user_id}`).then((d) => setRows(d.assignments)).catch(() => setRows([])), [user.user_id]);
  useEffect(() => { load(); }, [load]);
  const act = async (path, msg) => { try { await call(path, { method: "POST" }); toast.success(msg); load(); } catch (e) { toast.error(e.message); } };
  return (
    <div className="space-y-3" data-testid="user-assignments">
      <div className="text-sm"><b>{user.name}</b> <span className="text-[#6b7280]">{user.email} · home role: {user.role}</span></div>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-[#9ca3af]">
            <th className="py-1 pr-2">Role</th><th className="pr-2">Organization</th><th className="pr-2">Env</th><th className="pr-2">Status</th><th /></tr></thead>
          <tbody>
            {(rows || []).map((a) => (
              <tr key={a.id} className="border-t border-[#f0f1f3]" data-testid={`assignment-row-${a.id}`}>
                <td className="py-1.5 pr-2">{a.role_label}{a.is_default && <Star className="inline h-3.5 w-3.5 ml-1 text-amber-500" aria-label="Default" />}</td>
                <td className="pr-2">{a.org_name}</td>
                <td className="pr-2"><span className={`rounded px-1.5 text-[11px] font-semibold ${a.environment === "demo" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"}`}>{a.environment.toUpperCase()}</span></td>
                <td className={`pr-2 ${a.status === "active" ? "text-emerald-700" : "text-[#9ca3af]"}`}>{a.status}</td>
                <td className="whitespace-nowrap text-right">
                  {a.status === "active" && !a.is_default && <Button size="sm" variant="ghost" data-testid={`assignment-default-${a.id}`} onClick={() => act(`/admin/role-assignments/${a.id}/default?user_id=${user.user_id}`, "Default set")}>Make default</Button>}
                  {a.status === "active" && <Button size="sm" variant="ghost" className="text-red-600" data-testid={`assignment-revoke-${a.id}`} onClick={() => window.confirm(`Revoke ${a.role_label} — ${a.org_name}?`) && act(`/admin/role-assignments/${a.id}/revoke`, "Revoked")}>Revoke</Button>}
                </td>
              </tr>
            ))}
            {rows && !rows.length && <tr><td colSpan={5} className="py-3 text-[#9ca3af]">No extra role assignments — this user only has their home role.</td></tr>}
          </tbody>
        </table>
      </div>
      <AddForm user={user} workspaces={workspaces} onAdded={load} />
    </div>
  );
};

// Extends Users & Roles: scoped User + Role + Organization + Environment assignments and demo workspaces.
export const RoleAccessPanel = () => {
  const [open, setOpen] = useState(false);
  const [ws, setWs] = useState({ workspaces: [], people: [] });
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [picked, setPicked] = useState(null);
  const loadWs = useCallback(() => call("/admin/demo-workspaces").then(setWs).catch(() => {}), []);
  useEffect(() => { if (open) loadWs(); }, [open, loadWs]);
  useEffect(() => {
    if (!open || q.trim().length < 2) return setUsers([]);
    const t = setTimeout(() => call(`/admin/role-assignments/users?q=${encodeURIComponent(q)}`).then((d) => setUsers(d.users)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q, open]);
  return (
    <div className="mb-5 rounded-xl border border-[#e5e7eb] bg-white" data-testid="role-access-panel">
      <button onClick={() => setOpen(!open)} aria-expanded={open} data-testid="role-access-toggle"
        className="flex w-full min-h-[48px] items-center justify-between px-4 py-3 text-left">
        <span className="flex items-center gap-2 text-sm font-semibold text-[#111827]"><ShieldCheck className="h-4 w-4 text-[#007a9e]" />Multi-role access &amp; demo workspaces</span>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-5 border-t border-[#f0f1f3] p-4">
          <Workspaces ws={ws} reload={loadWs} />
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-[#111827]">Assign roles to a user</h3>
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9ca3af]" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a user by name or email" className="h-10 pl-9" data-testid="role-access-user-search" />
            </div>
            {users.length > 0 && !picked && (
              <ul className="max-w-md rounded-md border border-[#e5e7eb] text-[13px]">
                {users.map((u) => <li key={u.user_id}><button className="w-full min-h-[40px] px-3 py-2 text-left hover:bg-slate-50" data-testid={`role-access-user-${u.user_id}`} onClick={() => setPicked(u)}>{u.name} <span className="text-[#9ca3af]">{u.email} · {u.role}</span></button></li>)}
              </ul>
            )}
            {picked && (
              <div className="rounded-lg border border-[#e5e7eb] p-3">
                <button className="mb-2 text-[12px] text-[#007a9e] underline" onClick={() => setPicked(null)} data-testid="role-access-change-user">Change user</button>
                <UserAssignments user={picked} workspaces={ws.workspaces} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
